import { z } from "zod";

import { NanoIdRegex } from "./regexes/nano-id.regex";
import { UuidRegex } from "./regexes/uuid.regex";

/**
 * The two shapes {@link resolveChatLookup} branches on, as a schema.
 *
 * This is the "validate the input shape upstream" obligation that function's
 * header hands to its callers, in the one form every caller can share: a route
 * whose param reaches a chat lookup declares THIS, and a route that reads
 * `Chat.id` directly declares `z.string().uuid()`.
 *
 * A union rather than a bare string because the two shapes do not overlap — a
 * UUID is 36 characters with hyphens, a nanoId is exactly 11 as `XXXXX_XXXXX` —
 * so nothing is ambiguous and a value matching neither is refused here instead
 * of missing every row downstream.
 *
 * It lives beside the resolver rather than in either consumer's tree because
 * both surfaces answer for the same column: the internal
 * `GET /api/chatrooms/:chatRoomId` and every public-v1 `:conversationId` route,
 * which re-exports this as `ConversationIdSchema`.
 */
export const ChatIdOrNanoIdSchema = z.union(
  [
    z.string().regex(UuidRegex.pattern, { message: "Must be a UUID" }),
    z.string().regex(NanoIdRegex.pattern, { message: "Must be a nanoId (XXXXX_XXXXX)" })
  ],
  { error: "must be a UUID or nanoId" }
);

/**
 * Chat statuses that represent a "live" conversation — addressable for new
 * messages, exactly one of which may exist per `(organizationId, nanoId)` at any
 * moment (enforced by the partial unique index `chat_org_nanoid_live_unique`).
 *
 * A `DeploymentSession` inherits its `lookupTag` as the nanoId of every Chat it
 * creates over its lifetime (one live chat, plus the trailing ARCHIVED siblings
 * left behind by past resets). The uniqueness invariant is therefore scoped to
 * the live subset — historical chats keep their nanoId for customer continuity.
 */
export const LIVE_CHAT_STATUSES = ["OPEN", "RUNNING"] as const;
export type LiveChatStatus = (typeof LIVE_CHAT_STATUSES)[number];

/**
 * Branch a `Chat` lookup between the canonical UUID column and the public-facing
 * `nanoId` shortcode.
 *
 * `Chat.id` is `@db.Uuid` — feeding a non-UUID (e.g. a nanoId from an agent
 * skill payload like `4M7O9_BS76Q`) into `prisma.chat.findFirst({ where: { id } })`
 * makes the driver throw `Inconsistent column data: Error creating UUID, invalid
 * character` BEFORE the row is even queried. Branching at the boundary lets a
 * single port accept either shape without poisoning the read path.
 *
 * UUID-first is intentional: real `Chat.id` values always hit the primary-key
 * index; `nanoId` is the slower secondary path. The two shapes do not overlap
 * (UUID is 36 chars with hyphens; nanoId is exactly 11 chars `XXXXX_XXXXX`),
 * so the branch is deterministic.
 *
 * The caller is responsible for validating the input shape (UUID or nanoId)
 * upstream of this helper — it does NOT throw on garbage strings; it routes
 * them into the `nanoId` branch where they will simply miss any row.
 *
 * ### Determinism note
 *
 * A bare `findFirst({ where: { ...resolveChatLookup(input), status: { not: "DELETED" } } })`
 * is NOT deterministic when the input is a nanoId: the partial unique index
 * `chat_org_nanoid_live_unique` guarantees at most one OPEN/RUNNING chat, but
 * ARCHIVED siblings (from past session resets) can co-exist and the resolver
 * would non-deterministically return one of them when the live chat is missing.
 *
 * Use the backend `findChatByLookup` helper (in
 * `apps/backend/src/chats/infrastructure/lookup/find-chat-by-lookup.ts`) which
 * implements the two-step resolution: live chat first, then most-recent
 * ARCHIVED as fallback for historical references.
 *
 * @example
 * ```ts
 * import { findChatByLookup } from "src/chats/infrastructure/lookup/find-chat-by-lookup";
 * const resolved = await findChatByLookup(prisma, input, organizationId);
 * if (!resolved) throw new NotFoundException(...);
 * ```
 */
export function resolveChatLookup(idOrNanoId: string): { id: string } | { nanoId: string } {
  return UuidRegex.test(idOrNanoId) ? { id: idOrNanoId } : { nanoId: idOrNanoId };
}
